import { useState, type FormEvent, type ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { KeyRound, LogOut, ShieldAlert, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";

function FullScreenLoading() {
  return (
    <div className="center-screen">
      <div style={{ display: "grid", gap: 12, justifyItems: "center", color: "var(--muted)" }}>
        <div className="spinner" />
        <span className="small">Checking your session…</span>
      </div>
    </div>
  );
}

function MfaGate() {
  const { verifyMfa, signOut, user } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyMfa(code.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code did not work");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center-screen">
      <form className="access-card" onSubmit={submit}>
        <div className="kpi-icon" style={{ background: "var(--grape-wash)", color: "var(--grape-ink)" }}>
          <KeyRound size={20} />
        </div>
        <div>
          <h1 style={{ fontSize: 22 }}>Two-factor check</h1>
          <p className="muted small" style={{ marginTop: 4 }}>
            Enter the 6-digit code from your authenticator for {user?.email}.
          </p>
        </div>
        <input
          className="input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
          style={{ textAlign: "center", fontSize: 22, letterSpacing: 8, fontWeight: 700 }}
          autoFocus
        />
        {error && <div className="login-error">{error}</div>}
        <button className="btn btn-primary btn-block" disabled={busy || code.length !== 6}>
          {busy ? "Verifying…" : "Verify"}
        </button>
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => void signOut()}>
          <LogOut size={13} />
          Use another account
        </button>
      </form>
    </div>
  );
}

function AccessDenied() {
  const { user, signOut } = useAuth();
  return (
    <div className="center-screen">
      <div className="access-card">
        <div className="kpi-icon" style={{ background: "var(--danger-wash)", color: "var(--danger)" }}>
          <ShieldAlert size={20} />
        </div>
        <div>
          <h1 style={{ fontSize: 22 }}>Not a staff account</h1>
          <p className="muted small" style={{ marginTop: 4 }}>
            {user?.email} is signed in but has no console role. Ask an admin to add
            you to <span className="mono">admin_users</span>.
          </p>
        </div>
        <button className="btn btn-ghost" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

export function RequireStaff() {
  const { loading, session, mfaRequired, role } = useAuth();
  if (loading) return <FullScreenLoading />;
  if (!session) return <Navigate to="/login" replace />;
  if (mfaRequired) return <MfaGate />;
  if (!role) return <AccessDenied />;
  return <Outlet />;
}

export function StaffOnly({ children }: { children: ReactNode }) {
  const { role } = useAuth();
  if (role !== "admin") {
    return (
      <div className="banner banner-warn">
        <ShieldCheck size={16} />
        This action needs the admin role. You are signed in as {role}.
      </div>
    );
  }
  return <>{children}</>;
}
